import { z } from "zod";

export const AnyRecordSchema = z.record(z.string(), z.unknown());

export const StatusSchema = z.object({
  hasAdmin: z.boolean().default(false),
  authenticated: z.boolean().default(false),
  cafe24Connected: z.boolean().default(false),
  localOnly: z.boolean().optional(),
  databaseMode: z.string().optional(),
  assetVersion: z.string().optional(),
  user: z.unknown().nullable().optional(),
  cafe24Token: z.unknown().nullable().optional()
}).passthrough();

export const InventoryRowSchema = z.object({
  sku: z.string().optional(),
  readableAlias: z.string().optional(),
  productName: z.string().optional(),
  productCode: z.string().optional(),
  productKey: z.string().optional(),
  seasonCode: z.string().optional(),
  categoryName: z.string().optional(),
  colorwayName: z.string().optional(),
  colorwayCode: z.string().optional(),
  sizeName: z.string().optional(),
  sizeCode: z.string().optional(),
  onHandTotal: z.number().optional(),
  sellableTotal: z.number().optional(),
  reservedTotal: z.number().optional(),
  allocatedTotal: z.number().optional(),
  availableToSell: z.number().optional(),
  channelListings: z.array(AnyRecordSchema).optional(),
  balances: z.record(z.string(), z.number()).optional()
}).passthrough();

export const InventorySchema = z.object({
  rows: z.array(InventoryRowSchema).default([]),
  products: z.array(AnyRecordSchema).default([]),
  locations: z.array(AnyRecordSchema).default([]),
  channels: z.array(AnyRecordSchema).default([]),
  channelSummaries: z.array(AnyRecordSchema).default([]),
  channelProducts: z.array(AnyRecordSchema).default([]),
  movements: z.array(AnyRecordSchema).default([]),
  summary: AnyRecordSchema.default({})
}).passthrough();

export const FulfillmentQueueSchema = z.object({
  summary: AnyRecordSchema.default({}),
  items: z.array(AnyRecordSchema).default([])
}).passthrough();

export const BootstrapSchema = z.object({
  status: StatusSchema,
  inventory: InventorySchema,
  inventoryCounts: AnyRecordSchema.default({}),
  approvals: z.array(AnyRecordSchema).default([]),
  onlineMallApis: AnyRecordSchema.default({}),
  settingsRoles: AnyRecordSchema.default({}),
  auditLogs: AnyRecordSchema.default({ logs: [] }),
  fulfillmentQueue: FulfillmentQueueSchema,
  snapshots: AnyRecordSchema.default({}),
  assetVersion: z.string().optional()
}).passthrough();

export const ApiOkSchema = z.object({
  ok: z.boolean().optional(),
  message: z.string().optional()
}).passthrough();

export const BarcodeLookupSchema = z.object({
  ok: z.boolean().optional(),
  barcode: z.string().optional(),
  item: AnyRecordSchema.optional()
}).passthrough();

export const BarcodeOperationSchema = z.object({
  ok: z.boolean().optional(),
  dryRun: z.boolean().optional(),
  message: z.string().optional(),
  barcode: z.string().optional(),
  item: AnyRecordSchema.optional(),
  operation: AnyRecordSchema.optional(),
  preview: AnyRecordSchema.optional()
}).passthrough();

export type StatusPayload = z.infer<typeof StatusSchema>;
export type InventoryPayload = z.infer<typeof InventorySchema>;
export type InventoryRow = z.infer<typeof InventoryRowSchema>;
export type FulfillmentQueuePayload = z.infer<typeof FulfillmentQueueSchema>;
export type BootstrapPayload = z.infer<typeof BootstrapSchema>;
export type ApiOkPayload = z.infer<typeof ApiOkSchema>;
export type BarcodeLookupPayload = z.infer<typeof BarcodeLookupSchema>;
export type BarcodeOperationPayload = z.infer<typeof BarcodeOperationSchema>;
